import { ApiError } from "@/api/errors";
import { type FieldLeadCreate, type FieldLeadRead, createFieldLead } from "@/api/fieldLeads";
import { enqueueAction, isSessionDeferred } from "@/offline";
import { useMutation } from "@tanstack/react-query";
import { rememberFieldLead } from "./history";
import type { FieldLeadContactSource } from "./historyDb";

interface FieldLeadSubmission {
  body: FieldLeadCreate;
  machineLabel: string;
  contactSource: FieldLeadContactSource;
}

export type FieldLeadResult =
  | { mode: "sent"; receipt: FieldLeadRead }
  | { mode: "queued"; receipt: null; waitsFor: "signal" | "session" };

interface FieldLeadAttempt {
  submission: FieldLeadSubmission;
  queue: boolean;
}

export const FIELD_LEAD_LOADS_KEY = ["field-lead", "load"] as const;

export function fieldLeadLoadKey(clientLoadId: string): readonly string[] {
  return [...FIELD_LEAD_LOADS_KEY, clientLoadId];
}

export function useFieldLeadLoad(
  ownerKey: string,
  clientLoadId: string,
  onDone: (result: FieldLeadResult) => void,
) {
  return useMutation<FieldLeadResult, Error, FieldLeadAttempt>({
    mutationKey: fieldLeadLoadKey(clientLoadId),
    networkMode: "always",
    mutationFn: async ({ submission, queue }) => {
      if (queue) {
        await enqueueAction({ kind: "field-lead.create", body: submission.body });
        await remember(ownerKey, submission, "queued", null);
        return { mode: "queued", receipt: null, waitsFor: "signal" };
      }
      if (isSessionDeferred()) {
        return holdForSession(ownerKey, submission);
      }
      try {
        const receipt = await createFieldLead(submission.body);
        await remember(ownerKey, submission, "sent", receipt.id);
        return { mode: "sent", receipt };
      } catch (error) {
        if (error instanceof ApiError && error.status === 401) {
          return holdForSession(ownerKey, submission);
        }
        throw error;
      }
    },
    onSuccess: onDone,
  });
}

async function holdForSession(
  ownerKey: string,
  submission: FieldLeadSubmission,
): Promise<FieldLeadResult> {
  await enqueueAction({ kind: "field-lead.create", body: submission.body });
  await remember(ownerKey, submission, "queued", null);
  return { mode: "queued", receipt: null, waitsFor: "session" };
}

function remember(
  ownerKey: string,
  submission: FieldLeadSubmission,
  delivery: "sent" | "queued",
  leadId: string | null,
): Promise<void> {
  return rememberFieldLead({
    clientLoadId: submission.body.client_load_id,
    ownerKey,
    contactName: submission.body.contact_name,
    machineLabel: submission.machineLabel,
    loadedAt: new Date().toISOString(),
    delivery,
    leadId,
    contactSource: submission.contactSource,
  });
}
